import type { RecipientDetailsMasked, RecipientMaskedAttribute } from '@/types/payout';

export type ParsedRecipientMasked = RecipientDetailsMasked | string;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function pickSbpPhoneMask(raw: Record<string, unknown>): string | undefined {
  const candidates = [
    raw.phone_mask,
    raw.phone_masked,
    raw.phoneMask,
    raw['phone-mask'],
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) {
      return c;
    }
  }
  return undefined;
}

function pickAttributes(raw: Record<string, unknown>): RecipientMaskedAttribute[] | undefined {
  const value = raw.attributes;
  if (!Array.isArray(value)) {
    return undefined;
  }
  const attrs = value
    .map((item) => {
      if (!isRecord(item)) {
        return null;
      }
      const name = item.name;
      const attrValue = item.value;
      if (typeof name !== 'string' || typeof attrValue !== 'string') {
        return null;
      }
      const trimmedName = name.trim();
      if (!trimmedName) {
        return null;
      }
      return { name: trimmedName, value: attrValue } satisfies RecipientMaskedAttribute;
    })
    .filter((item): item is RecipientMaskedAttribute => item !== null);

  return attrs.length > 0 ? attrs : undefined;
}

/**
 * Разбор обрезанного / битого JSON, когда стандартный JSON.parse не проходит
 * (например обрыв на `"phone_m...`). Пробуем варианты с экранированными кавычками.
 */
function lenientParseRecipientMaskedJson(source: string): RecipientDetailsMasked | null {
  const variants = Array.from(new Set([source, source.replace(/\\"/g, '"')]));
  for (const src of variants) {
    const pick = (re: RegExp) => src.match(re)?.[1];
    const type = pick(/"type"\s*:\s*"(\w+)"/);
    if (type === 'card') {
      const number = pick(/"number"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1');
      const initials = pick(/"initials"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1');
      if (number != null && number !== '' && initials != null && initials !== '') {
        return { type: 'card', number, initials };
      }
      continue;
    }
    if (type === 'sbp') {
      const phone = pick(/"phone"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1');
      const initials = pick(/"initials"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1');
      if (phone != null && phone !== '' && initials != null && initials !== '') {
        const phone_mask =
          pick(/"phone_mask"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1') ??
          pick(/"phone_masked"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1') ??
          pick(/"phoneMask"\s*:\s*"((?:\\.|[^"\\])*)"/)?.replace(/\\(.)/g, '$1') ??
          phone;
        return { type: 'sbp', phone, phone_mask, initials };
      }
      continue;
    }
  }
  return null;
}

function tryParseObject(raw: Record<string, unknown>): RecipientDetailsMasked | null {
  const type = raw.type;
  if (type === 'card') {
    const number = raw.number;
    const initials = raw.initials;
    if (typeof number === 'string' && typeof initials === 'string') {
      return { type: 'card', attributes: pickAttributes(raw), number, initials };
    }
    return null;
  }
  if (type === 'sbp') {
    const phone = raw.phone;
    const initials = raw.initials;
    if (typeof phone !== 'string' || typeof initials !== 'string') {
      return null;
    }
    const phone_mask = pickSbpPhoneMask(raw) ?? phone;
    return { type: 'sbp', attributes: pickAttributes(raw), phone, phone_mask, initials };
  }
  return null;
}

/**
 * Разбирает `recipient_details_masked` из API: объект, JSON-строка или устаревшая plain-строка.
 */
export function parseRecipientDetailsMasked(raw: unknown): ParsedRecipientMasked | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }
    // Двойное JSON-кодирование: "\"{\\\"type\\\":\\\"sbp\\\"...\""
    if (trimmed.startsWith('"')) {
      try {
        const inner = JSON.parse(trimmed) as unknown;
        if (typeof inner === 'string') {
          return parseRecipientDetailsMasked(inner.trim());
        }
        if (isRecord(inner)) {
          const parsed = tryParseObject(inner);
          if (parsed) {
            return parsed;
          }
        }
      } catch {
        const loose = lenientParseRecipientMaskedJson(trimmed);
        if (loose) {
          return loose;
        }
      }
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return parseRecipientDetailsMasked(JSON.parse(trimmed) as unknown);
      } catch {
        const loose = lenientParseRecipientMaskedJson(trimmed);
        if (loose) {
          return loose;
        }
        return raw;
      }
    }
    const looseTail = lenientParseRecipientMaskedJson(trimmed);
    if (looseTail) {
      return looseTail;
    }
    return raw;
  }

  if (isRecord(raw)) {
    const parsed = tryParseObject(raw);
    if (parsed) {
      return parsed;
    }
    try {
      const loose = lenientParseRecipientMaskedJson(JSON.stringify(raw));
      if (loose) {
        return loose;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  return null;
}
